import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import mammoth from 'mammoth';

interface DocViewerProps {
  url: string;
  pageNumber?: number;
  citedPage?: number;
}

// Create a type for the methods we want to expose
export interface DocViewerHandle {
  previousPage: () => void;
  nextPage: () => void;
  getCurrentPage: () => number;
  goToPage: (page: number) => void;
}

const DocViewer = forwardRef<DocViewerHandle, DocViewerProps>(({ url, pageNumber = 1, citedPage }, ref) => {
  const [docHtml, setDocHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(citedPage || pageNumber);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [docPages, setDocPages] = useState<string[]>([]);

  // Determine if the file is actually a DOCX by checking the URL extension
  const isDocxFile = (url: string) => {
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.docx') || lowerUrl.endsWith('.doc');
  };

  // Override the getEmbedHtml function to always include the page parameter
  const getEmbedHtml = (url: string) => {
    const initialPage = citedPage || pageNumber;

    // For Azure Blob Storage links, use the Microsoft Office Online Viewer
    if (url.includes('publicstoragea08486eb.blob.core.windows.net')) {
      // Determine correct viewer based on file type
      const lowerUrl = url.toLowerCase();
      const isDocx = lowerUrl.endsWith('.docx') || lowerUrl.endsWith('.doc');

      if (isDocx) {
        // For Word docs, Office viewer uses 1-indexed pages with wdStartOn
        return `
          <div class="h-full w-full overflow-hidden"><iframe
              src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&ui=en-US&rs=en-US&wdHideHeaders=True&wdHideGridlines=True&wdHideHeaders=True&wdHideDots=True&wdAllowInteractivity=False&wdDownloadButton=False&wdPrint=False&wdToolbar=False&wdNavigationPane=False&wdStartOn=${initialPage}"
              class="w-full h-full border-none"
              title="Document Viewer">
            </iframe>
          </div>
        `;
      } else {
        // For PDF docs, Google Docs viewer uses 0-indexed pages with #page=
        const adjustedPage = initialPage - 1;
        return `
          <div class="h-full w-full overflow-hidden"><iframe
              src="https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true#page=${adjustedPage}"
              class="w-full h-full border-none"
              title="Document Viewer">
            </iframe>
          </div>
        `;
      }
    }

    // For other documents
    return `
      <div class="h-full w-full overflow-hidden"><iframe
          src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&ui=en-US&rs=en-US&wdHideHeaders=True&wdHideGridlines=True&wdHideHeaders=True&wdHideDots=True&wdAllowInteractivity=False&wdDownloadButton=False&wdPrint=False&wdToolbar=False&wdNavigationPane=False&wdStartOn=${initialPage}"
          class="w-full h-full border-none"
          title="Document Viewer">
        </iframe>
      </div>
    `;
  };

  useEffect(() => {
    const fetchDocument = async () => {
      if (!url) {
        setError('No document URL provided');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // For Azure Blob Storage documents, just use the embedded viewer
        if (url.includes('publicstoragea08486eb.blob.core.windows.net')) {
          // For Word documents, use Microsoft Office Online
          if (isDocxFile(url)) {
            const initialPage = citedPage || pageNumber;
            const embedHtml = `
              <div class="h-full w-full overflow-hidden">
              <iframe
                  src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&ui=en-US&rs=en-US&wdHideHeaders=True&wdHideGridlines=True&wdHideHeaders=True&wdHideDots=True&wdAllowInteractivity=False&wdDownloadButton=False&wdPrint=False&wdToolbar=False&wdNavigationPane=False&wdStartOn=${initialPage}"
                  class="w-full h-full border-none"
                  title="Document Viewer">
                </iframe>
              </div>
            `;
            setDocHtml(embedHtml);
            setDocPages([embedHtml]);
            setTotalPages(1);
            setIsLoading(false);
          } else {
            // For non-Word documents like PDFs
            // PDFs in direct iframe or browser view need page=N (1-indexed)
            // But embedded viewers like Google Docs may use #page=N (0-indexed)
            const initialPage = citedPage || pageNumber;
            let pdfParam = `#page=${initialPage}`;  // 1-indexed for most native PDF viewers

            const embedHtml = `
              <div class="h-full w-full overflow-hidden"><iframe
                  src="${url}${pdfParam}"
                  class="w-full h-full border-none"
                  title="Document Viewer">
                </iframe>
              </div>
            `;
            setDocHtml(embedHtml);
            setDocPages([embedHtml]);
            setTotalPages(1);
            setIsLoading(false);
          }
          return;
        }

        // Check if the URL is for a DOCX file
        if (!isDocxFile(url)) {
          // If not a DOCX, use Microsoft Office Viewer to display it
          const initialPage = citedPage || pageNumber;
          const embedHtml = `
            <div class="h-full w-full overflow-hidden"><iframe
                src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&ui=en-US&rs=en-US&wdHideHeaders=True&wdHideGridlines=True&wdHideHeaders=True&wdHideDots=True&wdAllowInteractivity=False&wdDownloadButton=False&wdPrint=False&wdToolbar=False&wdNavigationPane=False&wdStartOn=${initialPage}"
                class="w-full h-full border-none"
                title="Document Viewer">
              </iframe>
            </div>
          `;
          setDocHtml(embedHtml);
          setDocPages([embedHtml]);
          setTotalPages(1);
          setIsLoading(false);
          return;
        }

        // Otherwise, try to fetch and parse the DOCX file
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
        });

        if (!response.ok) {
          // If fetching fails, fall back to embedded viewer
          const embedHtml = getEmbedHtml(url);
          setDocHtml(embedHtml);
          setDocPages([embedHtml]);
          setTotalPages(1);
          setIsLoading(false);
          return;
        }

        const arrayBuffer = await response.arrayBuffer();

        // Use mammoth to convert the DOCX to HTML
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const html = result.value;

        if (!html) {
          throw new Error('Document appears to be empty or in an unsupported format');
        }

        // Store the HTML content with additional CSS reset to fix Word formatting
        const cleanedHtml = `<div class="h-full w-full overflow-hidden">${html}</div>`;
        setDocHtml(cleanedHtml);

        // Split content into pages based on paragraphs
        // This is a simple approach - real pagination would be more complex
        const paragraphs = html.split('<p>');
        const pagesArray = [];
        let pageContent = '';
        const paragraphsPerPage = 3; // Adjust this number based on desired page size

        for (let i = 0; i < paragraphs.length; i++) {
          if (i > 0) { // Skip the first empty element from the split
            pageContent += '<p>' + paragraphs[i];

            if (i % paragraphsPerPage === 0 && i > 0) {
              pagesArray.push(`<div class="h-full w-full overflow-hidden">${pageContent}</div>`);
              pageContent = '';
            }
          }
        }

        // Add the last page if there's content left
        if (pageContent) {
          pagesArray.push(`<div class="h-full w-full overflow-hidden">${pageContent}</div>`);
        }

        // Ensure we have at least one page
        if (pagesArray.length === 0) {
          pagesArray.push(cleanedHtml);
        }

        setDocPages(pagesArray);
        setTotalPages(pagesArray.length);
        setIsLoading(false);

        // Set the current page to the cited page, requested page number, or default to 1
        const initialPage = citedPage || pageNumber;
        setCurrentPage(Math.min(initialPage, pagesArray.length));
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err instanceof Error ? err.message : 'Failed to load document');

        // Fall back to embedded viewer
        const embedHtml = getEmbedHtml(url);
        setDocHtml(embedHtml);
        setDocPages([embedHtml]);
        setTotalPages(1);
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [url, pageNumber, citedPage]);

  // Navigation functions for multi-page documents
  function changePage(offset: number) {
    setCurrentPage(prevPage => Math.max(1, Math.min(totalPages, prevPage + offset)));
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function goToPage(page: number) {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  }

  // Expose methods to parent components through the ref
  useImperativeHandle(ref, () => ({
    previousPage,
    nextPage,
    getCurrentPage: () => currentPage,
    goToPage
  }));

  // CSS classes for styling document content
  const docContentStyles = `
    prose prose-sm max-w-none p-0
    [&>h1]:text-gray-800 [&>h1]:mt-5 [&>h1]:mb-2 [&>h1]:font-semibold [&>h1]:text-xl [&>h1]:border-none [&>h1]:bg-transparent [&>h1]:p-0
    [&>h2]:text-gray-800 [&>h2]:mt-5 [&>h2]:mb-2 [&>h2]:font-semibold [&>h2]:text-lg [&>h2]:border-none [&>h2]:bg-transparent [&>h2]:p-0
    [&>h3]:text-gray-800 [&>h3]:mt-5 [&>h3]:mb-2 [&>h3]:font-semibold [&>h3]:text-base [&>h3]:border-none [&>h3]:bg-transparent [&>h3]:p-0
    [&>p]:mb-4 [&>p]:p-0 [&>p]:border-none [&>p]:bg-transparent
    [&>ul]:ml-6 [&>ul]:mb-4 [&>ul]:list-disc
    [&>ol]:ml-6 [&>ol]:mb-4 [&>ol]:list-decimal
    [&>li]:mb-1
    [&>img]:max-w-full [&>img]:h-auto [&>img]:my-4
    [&>table]:w-full [&>table]:border-collapse [&>table]:my-4 [&>table]:border-none
    [&>table>tr>td]:border [&>table>tr>td]:border-gray-200 [&>table>tr>td]:p-2
    [&>table>tr>th]:border [&>table>tr>th]:border-gray-200 [&>table>tr>th]:p-2
    [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-none [&>iframe]:block [&>iframe]:m-0
  `;

  // Fallback iframe for error case
  useEffect(() => {
    if (error && document.querySelector('iframe')) {
      const iframe = document.querySelector('iframe');
      if (iframe && citedPage) {
        // Add page parameter to fallback iframe if needed
        if (iframe.src.includes('view.officeapps.live.com')) {
          // Ensure we don't add the parameter twice
          if (!iframe.src.includes('wdStartOn=')) {
            iframe.src = `${iframe.src}&wdStartOn=${citedPage}`;
          }
        } else if (iframe.src.toLowerCase().endsWith('.pdf')) {
          if (!iframe.src.includes('#page=')) {
            iframe.src = `${iframe.src}#page=${citedPage}`;
          }
        }
      }
    }
  }, [error, citedPage]);

  // Add an effect to manually navigate to the cited page when iframe loads
  useEffect(() => {
    if (!isLoading && citedPage && document.querySelector('iframe')) {
      const iframe = document.querySelector('iframe');
      if (iframe) {
        // For Office documents
        if (iframe.src.includes('view.officeapps.live.com') && !iframe.src.includes('wdStartOn=')) {
          iframe.src = `${iframe.src}&wdStartOn=${citedPage}`;
        }
        // For PDFs
        else if (iframe.src.toLowerCase().endsWith('.pdf') && !iframe.src.includes('#page=')) {
          iframe.src = `${iframe.src}#page=${citedPage}`;
        }
        // For Google Docs viewer
        else if (iframe.src.includes('docs.google.com/viewer') && !iframe.src.includes('#page=')) {
          const adjustedPage = citedPage - 1; // Google Docs viewer uses 0-indexed pages
          iframe.src = `${iframe.src}#page=${adjustedPage}`;
        }
      }
    }
  }, [isLoading, citedPage]);

  return (
    <div className="flex flex-col h-full">
      {error ? (
        <div className="flex items-center justify-center h-full text-center">
          <div className="bg-red-500/20 text-red-400  rounded-lg">
            <p>{error}</p>
            <p className="text-sm mt-2">URL: {url}</p>
            <div className="mt-4">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Open document in new tab
              </a>
            </div>

            <div className="mt-4 h-full w-full">
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&ui=en-US&rs=en-US&wdHideHeaders=True&wdHideGridlines=True&wdHideHeaders=True&wdHideDots=True&wdAllowInteractivity=False&wdDownloadButton=False&wdPrint=False&wdToolbar=False&wdNavigationPane=False&wdStartOn=${citedPage || pageNumber}`}
                className="w-full h-full border-none"
                title="Document Viewer"
              />
            </div>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
          <p className="ml-3 text-gray-400">Loading document...</p>
        </div>
      ) : (
        <div className="h-full w-full">
          {docPages.length > 0 ? (
            <div
              dangerouslySetInnerHTML={{
                __html: docPages[currentPage - 1]
                  ? docPages[currentPage - 1].replace('<div class="h-full w-full overflow-hidden">', `<div class="h-full w-full overflow-hidden ${docContentStyles}">`)
                  : docHtml.replace('<div class="h-full w-full overflow-hidden">', `<div class="h-full w-full overflow-hidden ${docContentStyles}">`)
              }}
              className="h-full w-full"
            />
          ) : (
            <div>
              <p>No content available in this document.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default DocViewer;
